"use client"

import { BlockchainFiltersMoleculeReatom as BlockchainFiltersMolecule } from "@/app/blockchains/filters/blockchain-filters-molecule.reatom"
import type { BlockchainFilter } from "@/app/blockchains/filters/blockchain-filters-scope"
import { ComingSoon } from "@/app/components/coming-soon"
import { Image } from "@/app/components/image"
import { useCollectionPageHref } from "@/app/navigation/collection"
import { CollectionPageType } from "@/app/navigation/collection/collection-page-type"
import { formatPercentage } from "@/app/percentage/utils"
import { PriceWrapper } from "@/app/price"
import type { FeaturedCollectionMiniature } from "@anaconda/core/nft-collection/collection/types"
import { Skeleton, cn } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import Link from "next/link"
import {
  Children,
  type ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { ChangeTrend } from "../leaderboard/view/change-trend"
import { useFeaturedCollections } from "./use-featured-collections"

/** Pixels per second for the infinite marquee. */
const AUTO_SCROLL_SPEED_PX_PER_SEC = 80

export const FeaturedCollectionsView = reatomComponent(
  ({ className }: { className?: string }) => {
    const { selectedBlockchain } = useMolecule(BlockchainFiltersMolecule)
    const blockchain = selectedBlockchain()

    const isMounteddRef = useRef(false)

    useEffect(() => {
      if (!isMounteddRef.current) {
        isMounteddRef.current = true
        // for some reason when component is hydrated in the middle of page end, scroll jumps,
        // defining constant heigh doesn't help
        window.scrollTo({ top: 0 })
      }
    }, [])

    return (
      <FeaturedCollectionContent
        blockchainFilter={blockchain}
        className={className}
      />
    )
  },
  "FeaturedCollectionsView",
)

function FeaturedCollectionContent({
  blockchainFilter,
  className,
}: { blockchainFilter: BlockchainFilter; className?: string }) {
  const featuredCollections = useFeaturedCollections(blockchainFilter)

  if (featuredCollections.length === 0) return null

  return (
    <FeaturedCollectionLayout className={className}>
      {featuredCollections.map((collection, index) => (
        <FeaturedCollectionItem
          key={
            collection.placeholder
              ? `placeholder-${index}`
              : `${collection.id}-${index}`
          }
          collection={collection}
        />
      ))}
    </FeaturedCollectionLayout>
  )
}

export function FeaturedCollectionsViewSkeleton({
  className,
}: { className?: string }) {
  return (
    <FeaturedCollectionLayout className={className} autoScroll={false}>
      <FeaturedCollectionItemSkeleton />
      <FeaturedCollectionItemSkeleton />
      <FeaturedCollectionItemSkeleton />
      <FeaturedCollectionItemSkeleton />
      <FeaturedCollectionItemSkeleton />
      <FeaturedCollectionItemSkeleton />
    </FeaturedCollectionLayout>
  )
}

function FeaturedCollectionLayout({
  children,
  className,
  autoScroll = true,
}: {
  children: ReactNode
  className?: string
  autoScroll?: boolean
}) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const pausedRef = useRef(false)
  const loopWidthRef = useRef(0)
  const [reducedMotion, setReducedMotion] = useState(false)
  const [contentOverflows, setContentOverflows] = useState(false)

  const itemCount = Children.count(children)
  const shouldLoop = autoScroll && !reducedMotion && contentOverflows

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)")
    const sync = () => setReducedMotion(media.matches)
    sync()
    media.addEventListener("change", sync)
    return () => media.removeEventListener("change", sync)
  }, [])

  useLayoutEffect(() => {
    if (!autoScroll || reducedMotion || itemCount === 0) {
      setContentOverflows(false)
      return
    }

    const scroller = scrollerRef.current
    if (!scroller) return

    const measure = () => {
      const items = scroller.children
      if (items.length < itemCount) {
        setContentOverflows(false)
        return
      }
      const first = items[0]
      const last = items[itemCount - 1]
      if (!(first instanceof HTMLElement) || !(last instanceof HTMLElement)) {
        setContentOverflows(false)
        return
      }
      const contentWidth =
        last.getBoundingClientRect().right - first.getBoundingClientRect().left
      const overflows = contentWidth > scroller.clientWidth + 1
      setContentOverflows((prev) => (prev === overflows ? prev : overflows))
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(scroller)
    return () => ro.disconnect()
  }, [autoScroll, reducedMotion, itemCount])

  useEffect(() => {
    if (!shouldLoop) {
      loopWidthRef.current = 0
      if (scrollerRef.current) scrollerRef.current.scrollLeft = 0
      return
    }

    const scroller = scrollerRef.current
    if (!scroller) return

    const measureLoopWidth = () => {
      const items = scroller.children
      // Direct siblings: [set1..., set2...]. First duplicate is at index itemCount.
      if (items.length < itemCount * 2) {
        loopWidthRef.current = 0
        return
      }
      const first = items[0]
      const firstDup = items[itemCount]
      if (
        !(first instanceof HTMLElement) ||
        !(firstDup instanceof HTMLElement)
      ) {
        loopWidthRef.current = 0
        return
      }
      loopWidthRef.current =
        firstDup.getBoundingClientRect().left -
        first.getBoundingClientRect().left +
        scroller.scrollLeft
    }

    measureLoopWidth()
    const ro = new ResizeObserver(measureLoopWidth)
    ro.observe(scroller)

    let frameId = 0
    let lastTs = 0

    const tick = (ts: number) => {
      if (!lastTs) lastTs = ts
      const dt = (ts - lastTs) / 1000
      lastTs = ts

      if (!pausedRef.current) {
        const loopWidth = loopWidthRef.current
        if (loopWidth > 0) {
          scroller.scrollLeft += AUTO_SCROLL_SPEED_PX_PER_SEC * dt
          if (scroller.scrollLeft >= loopWidth) {
            scroller.scrollLeft -= loopWidth
          }
        }
      }

      frameId = requestAnimationFrame(tick)
    }

    frameId = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(frameId)
      ro.disconnect()
    }
  }, [shouldLoop, itemCount])

  return (
    <div className={cn("flex flex-col gap-y-6 md:gap-y-8", className)}>
      <div
        ref={scrollerRef}
        className={cn(
          "flex gap-[10px] overflow-x-auto md:gap-5",
          shouldLoop && "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        )}
        onMouseEnter={() => {
          pausedRef.current = true
        }}
        onMouseLeave={() => {
          pausedRef.current = false
        }}
        onFocusCapture={() => {
          pausedRef.current = true
        }}
        onBlurCapture={(event) => {
          const next = event.relatedTarget
          if (!(next instanceof Node) || !event.currentTarget.contains(next)) {
            pausedRef.current = false
          }
        }}
      >
        {children}
        {shouldLoop ? cloneChildrenForLoop(children) : null}
      </div>
    </div>
  )
}

function cloneChildrenForLoop(children: ReactNode): ReactNode {
  return Children.map(children, (child, index) => {
    if (!isValidElement(child)) return child
    return cloneElement(child, {
      key: `loop-${child.key ?? index}`,
    })
  })
}

function FeaturedCollectionItem({
  collection,
}: { collection: FeaturedCollectionMiniature }) {
  if (collection.placeholder) {
    return <FeaturedCollectionPlaceholderItem collection={collection} />
  }

  return <FeaturedCollectionLinkItem collection={collection} />
}

function FeaturedCollectionLinkItem({
  collection,
}: { collection: FeaturedCollectionMiniature }) {
  if (!collection.id) return null

  const href = useCollectionPageHref(collection.id, CollectionPageType.ITEMS)

  return (
    <FeaturedCollectionItemWrapper>
      <Link
        href={href}
        prefetch={false}
        className={cn(
          "group h-full w-full rounded-lg border border-white/10 p-4 md:rounded-xl",
          "relative flex flex-col justify-between overflow-hidden",
        )}
      >
        <div className="absolute inset-0">
          <FeaturedCollectionMedia collection={collection} />
        </div>
        <div className="relative z-30 flex h-full flex-col justify-between">
          <h6 className="w-full truncate font-headline text-l md:text-lg">
            {collection.name}
          </h6>
          <div className="flex flex-col gap-[2px]">
            {collection.floorPrice && (
              <PriceWrapper
                className="text-base text-foreground/100 md:text-lg"
                amount={collection.floorPrice.amount}
                currency={collection.floorPrice.currency}
              />
            )}
            {/* No change to show — unknown, or one that rounds to 0% — leaves
                the floor price on its own. */}
            {collection.floorPriceChange !== undefined &&
              formatPercentage(collection.floorPriceChange) !== 0 && (
                <ChangeTrend
                  changePercent={collection.floorPriceChange}
                  className="text-xs md:text-sm"
                />
              )}
          </div>
        </div>
      </Link>
    </FeaturedCollectionItemWrapper>
  )
}

function FeaturedCollectionPlaceholderItem({
  collection,
}: { collection: FeaturedCollectionMiniature }) {
  return (
    <FeaturedCollectionItemWrapper>
      <ComingSoon>
        <div
          className={cn(
            "group h-full w-full rounded-lg border border-white/10 p-4 md:rounded-xl",
            "relative flex cursor-not-allowed flex-col justify-between overflow-hidden",
          )}
        >
          <div className="absolute inset-0">
            <FeaturedCollectionMedia collection={collection} />
          </div>
          <div className="relative z-30 flex h-full flex-col justify-between">
            <h6 className="w-full truncate font-headline text-l md:text-lg">
              {collection.name}
            </h6>
          </div>
        </div>
      </ComingSoon>
    </FeaturedCollectionItemWrapper>
  )
}

function FeaturedCollectionMedia({
  collection,
}: { collection: FeaturedCollectionMiniature }) {
  if (collection.mediaType === "video" && collection.video) {
    return (
      <video
        className="h-full w-full object-cover opacity-50 transition-transform duration-300 ease-out group-hover:scale-105"
        src={collection.video}
        poster={collection.image}
        autoPlay
        muted
        loop
        playsInline
        aria-label={collection.name}
      />
    )
  }

  return (
    <Image
      containerClassName="transition-transform duration-300 ease-out group-hover:scale-105 opacity-50 aspect-[27/20]"
      className="object-cover"
      imageSrc={collection.image}
      alt={collection.name}
    />
  )
}

function FeaturedCollectionItemSkeleton() {
  return (
    <FeaturedCollectionItemWrapper>
      <Skeleton className="h-full w-full rounded-2xl" />
    </FeaturedCollectionItemWrapper>
  )
}

function FeaturedCollectionItemWrapper({ children }: { children: ReactNode }) {
  return (
    <div
      className={cn(
        "aspect-[27/20] w-[calc((100%+10px)/3-10px)] min-w-[160px] md:w-[calc((100%+20px)/4-20px)] md:min-w-[200px] lg:w-[calc((100%+20px)/6-20px)]",
      )}
    >
      {children}
    </div>
  )
}
